/**
 * Lucy Add-to-Cart Module — One-Click Purchase from Chat
 * WowStore.live — Lucy Intelligence v5
 *
 * Adds "Add to Cart" buttons to Lucy's product cards.
 * Uses Shopify AJAX Cart API for seamless cart management.
 *
 * Integration:
 *   Loaded by theme.liquid after wowstore-ai-chatbot.liquid
 *   Hooks into: window.LucyCart (global API)
 *
 * Flow:
 *   1. Click "Add to Cart" on any Lucy product card
 *   2. Fetch product JSON from Shopify (for variant ID)
 *   3. POST to /cart/add.js
 *   4. Show success toast + update header cart count
 *   5. Fire analytics events via LucyAnalytics
 */
(function() {
  'use strict';

  /* Prevent duplicate initialization from Shopify section re-renders */
  if (window._lucyCartInstance) return;
  window._lucyCartInstance = Date.now();

  /* ═══════════════════ Config ═══════════════════ */
  var CART_ADD_URL = '/cart/add.js';
  var CART_URL = '/cart.json';
  var PRODUCT_JSON_SUFFIX = '.js'; /* .js endpoint includes 'available' field; .json does NOT */

  /* ═══════════════════ State ═══════════════════ */
  var variantCache = {}; /* handle → {variantId, variantTitle} */

  /* ═══════════════════ Cart API ═══════════════════ */

  /**
   * Fetch the first available variant ID for a product handle.
   * Caches results to avoid repeat requests.
   */
  function getVariantId(productUrl) {
    return new Promise(function(resolve, reject) {
      /* Extract handle from URL like /products/some-product-handle */
      var handle = extractHandle(productUrl);
      if (!handle) return reject(new Error('No product handle'));

      if (variantCache[handle]) return resolve(variantCache[handle]);

      fetch('/products/' + handle + PRODUCT_JSON_SUFFIX)
        .then(function(r) { return r.json(); })
        .then(function(data) {
          /* .js endpoint returns product directly, .json nests it */
          var product = data.product || data;
          if (!product || !product.variants || product.variants.length === 0) {
            return reject(new Error('No variants found'));
          }

          /* Find first available variant */
          var variant = null;
          for (var i = 0; i < product.variants.length; i++) {
            if (product.variants[i].available) {
              variant = product.variants[i];
              break;
            }
          }

          if (!variant) {
            /* All out of stock — use first variant anyway */
            variant = product.variants[0];
          }

          /* .js returns price in cents, .json in decimal string */
          var priceVal = variant.price;
          if (typeof priceVal === 'number' && priceVal > 100) {
            priceVal = (priceVal / 100).toFixed(2);
          }

          var result = {
            variantId: variant.id,
            variantTitle: variant.title,
            available: variant.available !== false,
            productId: product.id,
            productTitle: product.title,
            price: priceVal,
            handle: handle
          };

          variantCache[handle] = result;
          resolve(result);
        })
        .catch(reject);
    });
  }

  /**
   * Add item to Shopify cart via AJAX API.
   */
  function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    return fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: quantity }]
      })
    }).then(function(r) {
      if (!r.ok) throw new Error('Cart add failed: ' + r.status);
      return r.json();
    });
  }

  /**
   * Get current cart state (for count updates).
   */
  function getCart() {
    return fetch(CART_URL)
      .then(function(r) { return r.json(); });
  }

  /* ═══════════════════ UI Helpers ═══════════════════ */

  /**
   * Extract product handle from a URL path.
   * /products/my-product → my-product
   */
  function extractHandle(url) {
    if (!url) return null;
    var match = url.match(/\/products\/([^?#\/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Update all cart count badges in the header.
   * Shopify themes typically use .cart-count or similar.
   */
  function updateCartCount(count) {
    /* Common Shopify cart count selectors */
    var selectors = [
      '.cart-count',
      '.cart-count-bubble span',
      '[data-cart-count]',
      '.header__cart-count',
      '.js-cart-count',
      '#CartCount'
    ];

    selectors.forEach(function(sel) {
      var els = document.querySelectorAll(sel);
      els.forEach(function(el) {
        el.textContent = count;
        if (count > 0) el.style.display = '';
      });
    });

    /* Also try updating data attributes */
    var cartBubbles = document.querySelectorAll('[data-cart-count]');
    cartBubbles.forEach(function(el) {
      el.setAttribute('data-cart-count', count);
    });
  }

  /**
   * Show a success toast inside the Lucy chat window.
   */
  function showToast(message, isError) {
    var container = document.getElementById('aiChatMessages');
    if (!container) return;

    var toast = document.createElement('div');
    toast.style.cssText = [
      'background:' + (isError ? '#FEE2E2' : '#D1FAE5'),
      'color:' + (isError ? '#991B1B' : '#065F46'),
      'padding:10px 14px',
      'border-radius:10px',
      'margin:8px 0',
      'font-size:13px',
      'font-weight:500',
      'display:flex',
      'align-items:center',
      'gap:8px',
      'animation:fadeIn 0.3s ease'
    ].join(';');

    var icon = isError ? '\u274C' : '\u2705';
    toast.textContent = icon + ' ' + message;
    container.appendChild(toast);
    container.scrollTop = container.scrollHeight;

    /* Auto-remove after 5s */
    setTimeout(function() {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 5000);
  }

  /**
   * Show a "View Cart" button after successful add.
   */
  function showViewCartButton() {
    var container = document.getElementById('aiChatMessages');
    if (!container) return;

    var wrapper = document.createElement('div');
    wrapper.style.cssText = 'text-align:center;margin:8px 0';

    var btn = document.createElement('a');
    btn.href = '/cart';
    btn.textContent = '\uD83D\uDED2 View Cart & Checkout';
    btn.style.cssText = [
      'display:inline-block',
      'background:linear-gradient(135deg,#DB2777,#BE185D)',
      'color:white',
      'padding:10px 20px',
      'border-radius:8px',
      'text-decoration:none',
      'font-size:13px',
      'font-weight:600',
      'transition:opacity 0.2s'
    ].join(';');
    btn.addEventListener('mouseenter', function() { btn.style.opacity = '0.85'; });
    btn.addEventListener('mouseleave', function() { btn.style.opacity = '1'; });

    wrapper.appendChild(btn);
    container.appendChild(wrapper);
    container.scrollTop = container.scrollHeight;
  }

  /* ═══════════════════ Cart Button Injection ═══════════════════ */

  /**
   * Create an "Add to Cart" button for a product card.
   * Called by the chatbot when rendering products.
   */
  function createAddToCartButton(product) {
    var btn = document.createElement('button');
    btn.className = 'lucy-atc-btn';
    btn.type = 'button';
    btn.textContent = '\uD83D\uDED2 Add to Cart';
    btn.style.cssText = [
      'display:block',
      'width:100%',
      'margin-top:4px',
      'padding:5px 0',
      'background:#1F2937',
      'color:white',
      'border:none',
      'border-radius:6px',
      'font-size:11px',
      'font-weight:600',
      'cursor:pointer',
      'transition:all 0.2s',
      'letter-spacing:0.3px'
    ].join(';');

    btn.addEventListener('mouseenter', function() {
      btn.style.background = '#374151';
    });
    btn.addEventListener('mouseleave', function() {
      btn.style.background = '#1F2937';
    });

    btn.addEventListener('click', function(e) {
      e.stopPropagation();
      e.preventDefault();
      handleAddToCart(btn, product);
    });

    return btn;
  }

  /**
   * Handle the add-to-cart flow when button is clicked.
   */
  function handleAddToCart(btn, product) {
    /* Disable button and show loading */
    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = '\u23F3 Adding...';
    btn.style.opacity = '0.7';

    var productUrl = product.url || '';

    getVariantId(productUrl)
      .then(function(variant) {
        if (!variant.available) {
          showToast('Sorry, this item is currently out of stock', true);
          btn.textContent = 'Out of Stock';
          btn.style.background = '#9CA3AF';
          return Promise.reject(new Error('out_of_stock'));
        }
        return addToCart(variant.variantId, 1).then(function(cartData) {
          return { variant: variant, cartData: cartData };
        });
      })
      .then(function(result) {
        /* Success */
        btn.textContent = '\u2714 Added!';
        btn.style.background = '#059669';

        var title = (product.title || '').split(' - ')[0].substring(0, 40);
        showToast(title + ' added to your cart!');
        showViewCartButton();

        /* Update header cart count */
        getCart().then(function(cart) {
          updateCartCount(cart.item_count);
        });

        /* Fire analytics event */
        if (window.LucyAnalytics) {
          window.LucyAnalytics.track('add_to_cart', {
            product_id: product.id,
            product_title: product.title,
            price: product.price_value || product.price,
            variant_id: result.variant.variantId,
            source: 'lucy_chat'
          });
        }

        /* Reset button after 3s */
        setTimeout(function() {
          btn.textContent = '\uD83D\uDED2 Add Again';
          btn.style.background = '#1F2937';
          btn.style.opacity = '1';
          btn.disabled = false;
        }, 3000);
      })
      .catch(function(err) {
        if (err.message === 'out_of_stock') return;
        console.warn('[Lucy Cart] Error:', err.message);
        showToast('Could not add to cart. Try clicking the product instead.', true);
        btn.textContent = originalText;
        btn.style.opacity = '1';
        btn.disabled = false;
      });
  }

  /* ═══════════════════ Public API ═══════════════════ */

  window.LucyCart = {
    createButton: createAddToCartButton,
    addToCart: function(productUrl, quantity) {
      return getVariantId(productUrl).then(function(v) {
        return addToCart(v.variantId, quantity || 1);
      });
    },
    getCart: getCart,
    updateCount: updateCartCount
  };

  console.log('[Lucy Cart] Module loaded — one-click add-to-cart ready');
})();
